import { lazy, Suspense, useEffect, useState } from 'react';
import { SmoothScroll, useLenis } from '../lib/SmoothScroll';
import { ScrollTrigger } from '../lib/gsap';
import { scrollToId } from '../lib/scroll';
import { useScrollChoreography } from '../lib/choreography';
import { resetFiberState } from '../three/fiberState';
import { Nav } from '../components/layout/Nav';
import { Footer } from '../components/layout/Footer';
import { Hero } from '../sections/Hero';
import { Manifesto } from '../sections/Manifesto';
import { Work } from '../sections/Work';
import { Marquee } from '../sections/Marquee';
import { Expertise } from '../sections/Expertise';
import { Principles } from '../sections/Principles';
import { Vision } from '../sections/Vision';
import { Contact } from '../sections/Contact';
import { site } from '../content/site';

// three.js is loaded after the first paint so it never delays the hero.
const FiberCanvas = lazy(() => import('../three/FiberCanvas'));

/** Delay before the fiber field mounts: the hero headline reveal (about 1.5 s) plays first. */
const FIBERS_DELAY_MS = 1600;

/**
 * Load the fiber field after the hero entrance, when the browser is idle, so the
 * shader compilation never stalls the headline animation.
 */
function useDeferredMount(): boolean {
	const [ready, setReady] = useState(false);
	useEffect(() => {
		let idleId = 0;
		const timer = setTimeout(() => {
			if ('requestIdleCallback' in window) {
				idleId = window.requestIdleCallback(() => setReady(true), { timeout: 800 });
			} else {
				setReady(true);
			}
		}, FIBERS_DELAY_MS);
		return () => {
			clearTimeout(timer);
			if (idleId) window.cancelIdleCallback(idleId);
		};
	}, []);
	return ready;
}

/** Jump to the URL hash (e.g. /#realisations from /globe) once layout and pins are measured. */
function useInitialHash(): void {
	const lenis = useLenis();
	useEffect(() => {
		const id = window.location.hash.slice(1);
		if (!id) return;
		let cancelled = false;
		document.fonts.ready.then(() => {
			if (cancelled) return;
			ScrollTrigger.refresh();
			requestAnimationFrame(() => scrollToId(lenis, id, true));
		});
		return () => {
			cancelled = true;
		};
	}, [lenis]);
}

/** Restore every global the home page touches when leaving it (e.g. for /globe). */
function useDocumentReset(): void {
	useEffect(() => {
		const root = document.documentElement;
		return () => {
			root.style.removeProperty('--page-bg');
			delete root.dataset.chapter;
			resetFiberState();
		};
	}, []);
}

function HomeContent() {
	const fibers = useDeferredMount();
	useScrollChoreography();
	useInitialHash();
	useDocumentReset();

	return (
		<div className="grain relative">
			<a
				href="#main"
				className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-forest focus:px-5 focus:py-3 focus:text-mineral"
			>
				{site.nav.skipLink}
			</a>
			{fibers && (
				<Suspense fallback={null}>
					<FiberCanvas />
				</Suspense>
			)}
			<Nav />
			<main id="main" className="relative z-10 w-full max-w-full overflow-x-clip">
				<Hero />
				<Manifesto />
				<Work />
				<Marquee />
				<Expertise />
				<Principles />
				<Vision />
				<Contact />
			</main>
			<Footer />
		</div>
	);
}

export function HomePage() {
	return (
		<SmoothScroll>
			<HomeContent />
		</SmoothScroll>
	);
}
